import React from "react";
import { withState } from "recompose";
import { getThemeColor } from "../utils";
import styled from "styled-components";
import { getGraphNode } from "../selectors";
import Card from "../components/PictureCard";
import Flex from "../components/blocks/Flex";
import Helmet from "react-helmet";
import { P, Description, H1, H2, H3, H4, H5 } from "../components/typography";
import NavBar from "../components/NavBar";
import ContentSection from "../components/blocks/ContentSection";
import Quote from "../components/HeroQuote";
import PictureGrid from "../components/PictureGrid";
import KevinImage from "../../assets/kevin.jpg";
import Emphasis from "../components/atoms/Emphasis";
import Section from "../components/blocks/Section";
import Pad from "../components/blocks/Pad";
import Spacer from "../components/blocks/Spacer";
import favicon from "../../assets/logofavcon@2x.png";
import linkedinIcon from "../../assets/linkedin-icon.png";
import emailIcon from "../../assets/email-icon.png";

const Icon = styled.img`
  height: 2rem;
  padding-right: 1rem;
  padding-top: 1rem;
`;

const Memoriam = styled.div`
  text-style: italic;
  font-size: 1.3rem;
  @media (max-width: 1100px) {
    display: none;
  }
`;

const BioOverlay = styled.div`
  position: fixed;
  left: 0;
  top: 2rem;
  padding-bottom: 4rem;
  right: 0;
  overflow: hidden;
  padding: 2rem 3rem;
  z-index: 3;
  font-size: 1rem;
  opacity: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  transition: opacity 300ms;
  pointer-events: ${props => (props.visible ? "inherit" : "none")};
  opacity: ${props => (props.visible ? 1 : 0)};

  @media (max-width: 700px) {
    bottom: 2rem;
  }

  &:hover {
    opacity: 1;
  }
`;

const BioOverlayBody = styled.div`
  padding: 2rem 3rem;
  max-width: 720px;
  background-color: ${getThemeColor("purpleDark")};
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const BioText = P.extend`font-size: 1.3rem;`;

const JoinUsWrapper = styled.div`
  margin-top: -5rem;

  ${props => props.theme.media.mobile`
    margin-top: 0;
  `};
`;

const InvestorLogos = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3rem;
  flex-wrap: wrap;
  width: 100%;
  margin: 2rem auto 0 -2rem;
  padding: 0;
  ${props => props.theme.media.mobile`
    justify-content: flex-start;
    gap: 2rem;
    margin: 2rem auto 0;
    padding: 0 1.5rem;
    max-width: 100%;
    overflow: hidden;
  `};
`;

const InvestorLogo = styled.img`
  max-height: ${props => props.square ? '64px' : '42px'};
  max-width: 156px;
  object-fit: contain;
  filter: grayscale(100%);
  opacity: 1;
  ${props => props.theme.media.mobile`
    max-height: ${props => props.square ? '51px' : '34px'};
    max-width: 125px;
  `};
`;

const getInitials = name =>
  name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

const makePlaceholder = name => {
  const initials = getInitials(name);
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="#b0b8c4" width="400" height="300"/><text fill="#fff" font-family="Arial,sans-serif" font-size="120" x="200" y="160" text-anchor="middle" dominant-baseline="middle">${initials}</text></svg>`
  )}`;
};

const getShortBio = bio => {
  const sentences = bio.split(/(?<=[.!?])\s+/);
  return sentences[0].trim().endsWith("Dr.") ? sentences.slice(0, 2).join(" ") : sentences[0];
};

const enhance = withState("selectedPerson", "selectPerson", null);

class TeamPage extends React.Component {
  render() {
    const content = getGraphNode(this.props.data, "TeamPage");

    const team = content.teamMembers.map(person => ({
        ...person,
        bio: person.bio.bio,
        description: person.position,
        src: person.image ? person.image.responsiveResolution.src : KevinImage
      })),
      board = content.boardMembers.map(person => ({
        ...person,
        bio: person.bio.bio,
        description: person.shortBio || getShortBio(person.bio.bio),
        src: person.image ? person.image.responsiveResolution.src : makePlaceholder(person.name)
      })),
      advisors = content.advisoryBoard.map(person => ({
        ...person,
        bio: person.bio.bio,
        description: person.shortBio || getShortBio(person.bio.bio),
        src: person.image ? person.image.responsiveResolution.src : makePlaceholder(person.name)
      }));

    return (
      <div>
        <Helmet
          title="Contraline Team"
          meta={[{ name: "Contraline", content: "Team" }]}
        >
          <link rel="icon" type="image/png" href={favicon} />
        </Helmet>
        <NavBar color="dark" />
        <Quote
          padding={"1rem"}
          serif
          color="white"
          quote={content.heroQuote}
          author={content.heroAuthor}
          style={{
            minHeight: "20rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          }}
        />
        <Section color="white">
          <Pad v={2}>
            <Emphasis>
              <H4>
                {content.ourTeamHeader}
              </H4>
            </Emphasis>
          </Pad>
          <PictureGrid persons={team} selectPerson={this.props.selectPerson} />
        </Section>
        <BioOverlay
          visible={!!this.props.selectedPerson}
          onClick={e => {
            console.log(e.target);
            if (e.target.className.indexOf("overlay") > -1)
              this.props.selectPerson(null);
          }}
          className="overlay"
        >
          {this.props.selectedPerson &&
            <BioOverlayBody>
              <Flex>
                <H3 color="white" style={{ flex: 1 }}>
                  {this.props.selectedPerson.name}
                </H3>
                <H3
                  color="white"
                  onClick={() => this.props.selectPerson(null)}
                  style={{ cursor: "pointer" }}
                >
                  X
                </H3>
              </Flex>
              {this.props.selectedPerson.position &&
                <H4 color="white">
                  {this.props.selectedPerson.position}
                </H4>}
              <Spacer span={0.4} />
              <BioText color="white">
                {this.props.selectedPerson.bio}
              </BioText>
              <Flex justify="flex-start">
                {this.props.selectedPerson.linkedin &&
                  <a href={this.props.selectedPerson.linkedin}>
                    <Icon src={linkedinIcon} />
                  </a>}
                {this.props.selectedPerson.email &&
                  <a href={`mailto:${this.props.selectedPerson.email}`}>
                    <Icon src={emailIcon} />
                  </a>}
              </Flex>
            </BioOverlayBody>}
        </BioOverlay>
        <JoinUsWrapper>
          <ContentSection
            color="white"
            titleText={content.sectionTitle}
            bodyText={content.sectionBody.sectionBody.split("|")}
            link={{
              label: content.sectionLink,
              to: "/careers"
            }}
          />
        </JoinUsWrapper>
        <Section color="greyLight">
          <Pad v={3}>
            <Emphasis>
              <H4>{(content.investorsTitle) || "Our Investors"}</H4>
            </Emphasis>
          </Pad>
          <InvestorLogos>
            {content.investors && content.investors.length > 0
              ? content.investors.map((inv, i) =>
                  <InvestorLogo
                    key={i}
                    src={inv.logo ? inv.logo.responsiveResolution.src : ""}
                    alt={inv.name}
                    onLoad={e => {
                      const img = e.target;
                      if (img.naturalWidth / img.naturalHeight < 1.5) {
                        img.style.maxHeight = '64px';
                      }
                    }}
                  />
                )
              : [1, 2, 3, 4, 5].map(i =>
                  <InvestorLogo
                    key={i}
                    src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_272x92dp.png"
                    alt="Investor logo placeholder"
                  />
                )
            }
          </InvestorLogos>
          <Spacer span={4} />
          <Pad v={3}>
            <Emphasis>
              <H4>
                {content.directorsTitle}
              </H4>
            </Emphasis>
          </Pad>
          <PictureGrid persons={board} selectPerson={this.props.selectPerson} />
          <Pad v={4}>
            <Pad v={3}>
              <Emphasis>
                <H4>
                  {content.advisoryBoardTitle}
                </H4>
              </Emphasis>
            </Pad>
            <PictureGrid padLarge={false} persons={advisors} selectPerson={this.props.selectPerson} />
          </Pad>
        </Section>
        <Card
          picture={content.johnHerrImage.responsiveResolution.src}
          color="herrBlue"
          title={
            <Flex>
              <span>
                {content.johnHerrTitle}
              </span>
              <Memoriam>
                {content.inMemoriam}
              </Memoriam>
            </Flex>
          }
          subtitle={content.johnHerrSubtitle}
          text={content.johnHerrBody.johnHerrBody}
        />
      </div>
    );
  }
}

export default enhance(TeamPage);

export const pageQuery = graphql`
  query TeamPageQuery {
    allContentfulTeamPage {
      edges {
        node {
          id
          heroQuote
          heroAuthor
          ourTeamHeader
          sectionTitle
          sectionBody {
            sectionBody
          }
          sectionLink
          directorsTitle
          advisoryBoardTitle
          johnHerrImage {
            responsiveResolution(width: 1000, quality: 70) {
              src
            }
          }
          inMemoriam
          johnHerrTitle
          johnHerrSubtitle
          johnHerrBody {
            johnHerrBody
          }
          teamMembers {
            id
            name
            position
            linkedin
            email
            bio {
              bio
            }
            image {
              id
              responsiveResolution(width: 500, quality: 70) {
                src
              }
            }
          }
          advisoryBoard {
            id
            name
            shortBio
            position
            linkedin
            email
            bio {
              bio
            }
            image {
              responsiveResolution(width: 500, quality: 70) {
                src
              }
            }
          }
          boardMembers {
            id
            name
            shortBio
            position
            linkedin
            email
            bio {
              bio
            }
            image {
              responsiveResolution(width: 500, quality: 70) {
                src
              }
            }
          }
          investorsTitle
          investors {
            id
            name
            logo {
              responsiveResolution(width: 200, quality: 70) {
                src
              }
            }
          }
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/pages/team.js